import { discoveryApiRef, useApi, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api'
import React, { useEffect, useState } from 'react'
import axios from 'axios';
import { Header } from '@backstage/core-components';
import { Avatar, Box, Typography, makeStyles } from '@material-ui/core';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Loader from '../loader/Loader';
import BoxComponent from './CustomHeatmap';

const useStyles = makeStyles({
  containerdiv: {
    display: 'flex', flexDirection: 'row', marginTop: '20px', justifyContent: 'center'
  },
  maindiv: {
    padding: '20px', height: '80vh', overflow: "scroll"
  },
  usersdiv: {
    display: 'flex', flexDirection: 'column', width: '100%', height: '80vh'
  },
  memberdiv: {
    display: 'flex', justifyContent: 'flex-start', minWidth: '250px', textOverflow: 'ellipsis'
  },
  membername: {
    padding: '10px', fontWeight: 'bold'
  },
  commitsdiv: {
    justifyContent: 'center', width: '550px', marginLeft: '20px', marginTop: '20px'
  },
  customMenuItem: {
    display: 'block',
    padding: '5px 20px',
  },
})

const getRandomColor = () => {
  const letters = '0123456789ABCDEF';
  let color = '#';
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

const CustomAvatar = ({ initial }) => {
  const randomColor = getRandomColor();
  return (
    <Avatar style={{ backgroundColor: `${randomColor}`, textTransform: 'capitalize' }}>
      {initial}
    </Avatar>
  )
}

const Heatmap = () => {
  const discoveryApi = useApi(discoveryApiRef)
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef)

  const [contributors, setContributors] = useState<any>([]);
  const [data, setData] = useState<any>([]);
  const [dimension, setDimension] = useState('commits');
  const [isLoading, setIsLoading] = useState(true);
  const [tenants, setTenants] = useState<any[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [orgMap, setOrgMap] = useState<{ [key: string]: string }>({});
  const [role, setRole] = useState('');

  const styles = useStyles();

  // fetch tenants and orgs once
  useEffect(() => {
    const fetchTenantsAndOrgs = async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('user');
        const tenantsRes = await axios.get(`${baseUrl}/tenants`);
        const orgsRes = await axios.get(`${baseUrl}/getOrg`);

        setTenants(tenantsRes.data || []);

        // map tenantId -> github_org
        const mapping: { [key: string]: string } = {};
        orgsRes.data.forEach((org: any) => {
          mapping[org.tenantId] = org.github_org;
        });
        setOrgMap(mapping);

        if (tenantsRes.data.length > 0) {
          setSelectedTenant(String(tenantsRes.data[0].id)); // default first tenant
        }
      } catch (e) {
        console.error('Error fetching tenants or orgs:', e);
      }
    };
    fetchTenantsAndOrgs();
  }, [discoveryApi]);

  // fetch heatmap data whenever tenant or dimension changes
  useEffect(() => {
    if (!selectedTenant) return;
    const fetchData = async () => {
      try {
        const details = await identityApi.getBackstageIdentity();
        const userRole = details.ownershipEntityRefs.find(ref =>
          ref.startsWith('role:')
        )?.split(':')[1];
        const tenantId = details.ownershipEntityRefs.find(ref =>
          ref.startsWith('tenantId:')
        )?.split(':')[1];
        setRole(userRole);
        setIsLoading(true);
        const baseUrl = await discoveryApi.getBaseUrl('user');
        let githubOrgName 
        if (role == 'SuperAdmin'){
          githubOrgName = orgMap[selectedTenant];
        }
        else{
          githubOrgName = orgMap[tenantId];
          setSelectedTenant(tenantId)
        }
        
        if (!githubOrgName) {
          console.warn("No githubOrgName found for tenant:", selectedTenant);
          setContributors([]);
          setData([]);
          return;
        }

        let url = `${baseUrl}/org/${githubOrgName}/members-commits?tenantId=${selectedTenant}`;
        if (dimension === "changes") {
          url = `${baseUrl}/org/${githubOrgName}/members-commits-changes?tenantId=${selectedTenant}`;
        } else if (dimension === "openPR") {
          url = `${baseUrl}/org/${githubOrgName}/members-open-pr?tenantId=${selectedTenant}`;
        } else if (dimension === "closedPR") {
          url = `${baseUrl}/org/${githubOrgName}/members-closed-pr?tenantId=${selectedTenant}`;
        }

        const fetchresponse = await fetchApi.fetch(url, { method: 'GET' });
        if (!fetchresponse.ok) {
          const errorText = await fetchresponse.text();
          throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
        }
        const response = await fetchresponse.json();
        console.log('Fetched data:', response);

        let transformed: any[] = [];
        if (dimension === "changes") {
          transformed = transformDataForChanges(response.commitStats || []);
        } else if (dimension === "openPR" || dimension === "closedPR") {
          transformed = transformDataForPR(response.commitStats || []);
        } else {
          transformed = transformDataForHeatmap(response.commitStats || []);
        }

        const users = [...new Set((response.commitStats || []).map(commit => commit.user))];
        setContributors(users);
        setData(transformed);

      } catch (error) {
        console.error("Error fetching heatmap data:", error);
        setContributors([]);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedTenant, dimension, discoveryApi, fetchApi, orgMap]);

  const transformDataForHeatmap = (userData: any[]) => {
    const heatmapData: { [key: string]: { [key: string]: number } } = {};
    userData.forEach(entry => {
      const { user, commitCount, date } = entry;
      if (!heatmapData[user]) heatmapData[user] = {};
      heatmapData[user][date] = (heatmapData[user][date] || 0) + commitCount;
    });
    return Object.entries(heatmapData).map(([user, commitsByDate]) => ({
      user,
      heatmapData: Object.entries(commitsByDate).map(([date, count]) => ({ date, count }))
    }));
  };

  const transformDataForChanges = (userData: any[]) => {
    const changeData: { [key: string]: { [key: string]: number } } = {};
    userData.forEach(entry => {
      const { user, changes, date } = entry;
      if (!changeData[user]) changeData[user] = {};
      changeData[user][date] = (changeData[user][date] || 0) + changes;
    });
    return Object.entries(changeData).map(([user, changesByDate]) => ({
      user,
      heatmapData: Object.entries(changesByDate).map(([date, count]) => ({ date, count }))
    }));
  };

  const transformDataForPR = (userData: any[]) => {
    const prData: { [key: string]: { [key: string]: number } } = {};
    userData.forEach(entry => {
      const { user, prRequests, date } = entry;
      if (!prData[user]) prData[user] = {};
      prData[user][date] = (prData[user][date] || 0) + prRequests;
    });
    return Object.entries(prData).map(([user, prsByDate]) => ({
      user,
      heatmapData: Object.entries(prsByDate).map(([date, count]) => ({ date, count }))
    }));
  };

  const colorRanges = [
    { min: 1, max: 4, color: 'rgb(255, 64, 64)' },
    { min: 5, max: 10, color: 'rgb(163, 206, 168)' },
    { min: 11, max: Infinity, color: 'rgb(114, 186, 121)' }
  ];

  return (
    <div>
    <Header title='HeatMap' />
    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, paddingLeft: 16 }}>
      *Note: Developers with 0 commits and Prs for current month are not shown in the heatmap.
    </Typography>
    <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '20px', gap: '20px' }}>
      {/* Tenant Dropdown */}
      {role === "SuperAdmin" && (
      <FormControl>
        <Select
          value={selectedTenant}
          onChange={(e) => setSelectedTenant(e.target.value)}
          MenuProps={{
            PaperProps: {
              style: {
                display: 'block',
              },
            },
          }}
        >
          {tenants.map((tenant) => (
            <MenuItem 
              key={tenant.id} 
              value={String(tenant.id)} 
              style={{ display: 'block', whiteSpace: 'normal' }}
            >
              {tenant.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      )}
      {/* Dimension Dropdown (now beside tenant) */}
      <FormControl>
        <Select
          value={dimension}
          onChange={(e) => setDimension(e.target.value as string)}
        >
          <MenuItem value="commits" className={styles.customMenuItem}>Number of Commits</MenuItem>
          <MenuItem value="changes" className={styles.customMenuItem}>Number of Changes</MenuItem>
          <MenuItem value="openPR" className={styles.customMenuItem}>Open Pull Requests</MenuItem>
          <MenuItem value="closedPR" className={styles.customMenuItem}>Closed Pull Requests</MenuItem>
        </Select>
      </FormControl>
    </div>


      <div className={styles.containerdiv}>
        {colorRanges.map((range, index) => (
          <div style={{ marginRight: '20px' }} key={index}>
            <div style={{ backgroundColor: range.color, width: '20px', height: '20px', marginRight: '5px' }}></div>
            <span>{range.min} - {range.max !== Infinity ? range.max : 'More than 10'}</span>
          </div>
        ))}
      </div>

      {isLoading ? (
        <Loader />
      ) : contributors.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="60vh" // adjust for vertical centering
          >
            <Typography variant="h6" color="textSecondary">
              No data available.
            </Typography>
          </Box>
      ) : (
        <div className={styles.maindiv}>
          <div className={styles.usersdiv}>
            {contributors.map((user) => (
              <Box style={{ marginLeft: '50px' }} key={user}>
                <Box style={{ display: 'flex' }}>
                  <Box className={styles.memberdiv}>
                    <CustomAvatar initial={user.charAt(0)} />
                    <Typography className={styles.membername}>
                      {user}
                    </Typography>
                  </Box>
                  <Box className={styles.commitsdiv}>
                    <BoxComponent data={data.find(item => item.user === user)?.heatmapData || []} />
                  </Box>
                </Box>
              </Box>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Heatmap;